<template>
  <v-form
    v-if="appointment && appointment.car"
    ref="form">
    <tab-title
      :car-id="appointment.car.id"
      text="評価" />
    <v-select
      v-model="appointment.car.condition"
      :items="conditionOptions"
      :hint="score.scoreCondition ? `楽天Car車買取推奨評価：${score.scoreCondition}` : ''"
      :rules="[$rules.isRequired]"
      label="状態評価 *"
      data-label-scroll="condition"
      persistent-hint
      @change="(value) => updateCar({
        field: 'condition',
        value
      }
      )"
    />
    <v-select
      v-model="appointment.car.evaluationExterior"
      :items="evaluationOptions"
      :hint="score.scoreExterior ? `楽天Car車買取推奨評価：${score.scoreExterior}` : ''"
      :rules="[$rules.isRequired]"
      label="外装評価 *"
      data-label-scroll="evaluationExterior"
      persistent-hint
      @change="(value) => updateCar({
        field: 'evaluationExterior',
        value
      }
      )"
    />
    <v-select
      v-model="appointment.car.evaluationInterior"
      :items="evaluationOptions"
      :hint="score.scoreInterior ? `楽天Car車買取推奨評価：${score.scoreInterior}` : ''"
      :rules="[$rules.isRequired]"
      label="内装評価 *"
      data-label-scroll="evaluationInterior"
      persistent-hint
      @change="(value) => updateCar({
        field: 'evaluationInterior',
        value
      }
      )"
    />
    <span data-label-scroll="urikiriMinPrice">
      <v-text-field
        v-model="appointment.car.urikiriMinPrice"
        :rules="[$rules.isRequired, $rules.isNumber]"
        :error-messages="urikiriPriceMutedMessages"
        label="最低希望落札価格 下限金額（推奨）*"
        prefix="¥"
        @blur="updateCar({field: 'urikiriMinPrice', value: appointment.car.urikiriMinPrice })"
      />
    </span>
    <span data-label-scroll="urikiriMaxPrice">
      <v-text-field
        v-model="appointment.car.urikiriMaxPrice"
        :rules="[$rules.isRequired, $rules.isNumber]"
        :error-messages="urikiriPriceMessages"
        label="最低希望落札価格 上限金額（推奨）*"
        prefix="¥"
        @blur="updateCar({field: 'urikiriMaxPrice', value: appointment.car.urikiriMaxPrice })"
      />
    </span>
    <v-text-field
      v-model="appointment.car.urikiriComment"
      :rules="[$rules.isRequired]"
      label="コメント*"
      data-label-scroll="urikiriComment"
      type="text"
      @blur="updateUrikiriComment"
    />
    <v-divider class="my-5" />
    <template v-if="canFinish">
      <v-btn
        color="primary"
        block
        large
        class="my-0 subheading"
        @click="$store.commit('toggleSentShuppinhyoDialog')"
      >出品票送信</v-btn>
      <span class="red--text">
        ※内容を修正した場合は、再度送信ボタンを押してください
      </span>
      <v-btn
        v-if="canDisplayInspectionCompletedBtn"
        color="info"
        block
        large
        class="mt-3"
        @click="$store.commit('toggleFinishedDialog')"
      >検査完了</v-btn>
    </template>
  </v-form>
</template>

<script>

import {
  getCarWith,
  GET_SCORES,
  GET_REMAINING_FIELDS,
} from '../../queries';

import {
    conditionOptions,
    evaluationOptions,
  } from '../../options';

import { defaultRemainingFields } from '../InspectionPage';

const parseIntVal = val => parseInt(`${val}`, 10);

export default {
  props: {
    appointmentId: {
      type: [String, Number],
      default: '',
    },
  },
  apollo: {
    appointment: {
      fetchPolicy: 'network-only',
      query: getCarWith(['condition', 'evaluationExterior', 'evaluationInterior', 'urikiriMinPrice', 'urikiriMaxPrice', 'urikiriComment']),
      variables() {
        return {
          id: parseInt(this.appointmentId, 10),
        };
      },
    },
    score: {
      query: GET_SCORES,
      fetchPolicy: 'network-only',
      skip() {
        return !this.appointment || !this.appointment.car;
      },
      variables() {
        return {
          carId: parseInt(this.appointment.car.id, 10),
        };
      },
    },
    remainingFields: {
      query: GET_REMAINING_FIELDS,
      fetchPolicy: 'network-only',
      variables() {
        return {
          appointmentId: parseInt(this.appointmentId, 10),
          tabs: ['Owner', 'Basic', 'Equipment', 'SalesPoints', 'Photos',
            'Damages', 'Attention', 'Repairs', 'ConfirmForSelfIns', 'Evaluation'],
        };
      },
      update({ remainingFields }) {
        this.loaded = true;
        const output = {
          ...defaultRemainingFields,
          ...remainingFields.reduce((acc, cur) => ({
            ...acc,
            [cur.tab.replace(/[A-Z0-9]/g, a => `-${a.toLowerCase()}`).replace(/^-/, '')]: cur.count,
          }), {}),
        };

        if (!parseInt(this.appointment.selfInspectionFlag, 10)) {
          delete output['confirm-for-self-ins'];
        }
        return output;
      },
    },
  },
  data() {
    return {
      tab: 'evaluation',
      conditionOptions,
      evaluationOptions,
      score: {
        scoreCondition: null,
        scoreExterior: null,
        scoreInterior: null,
      },
      remainingFields: defaultRemainingFields,
    };
  },
  computed: {
    canFinish() {
      if (!this.remainingFields
        || this.isAnyPriceNotNumber(this.appointment.car)
        || this.isMinMoreThanMax(this.appointment.car)
        || this.isEmptyOrSpaces(this.appointment.car)
        ) return false;

      return Object.values(this.remainingFields).every(v => v === 0);
    },
    canDisplayInspectionCompletedBtn() {
      if (!Number(this.appointment.selfInspectionFlag)
        || Number(this.appointment.sentShuppinhyoFlag)) return true;
      return false;
    },
    isUrikiriPriceValid() {
      return this.isAnyPriceNotNumber(this.appointment.car) ||
        !this.isMinMoreThanMax(this.appointment.car);
    },
    urikiriPriceMessages() {
      return this.isUrikiriPriceValid ? [] : ['下限金額が上限金額以下になるように入力してください。'];
    },
    urikiriPriceMutedMessages() {
      return this.isUrikiriPriceValid ? [] : [''];
    },
  },
  methods: {
    isAnyPriceNotNumber({ urikiriMinPrice, urikiriMaxPrice }) {
      return Number.isNaN(parseIntVal(urikiriMinPrice)) ||
        Number.isNaN(parseIntVal(urikiriMaxPrice));
    },
    isMinMoreThanMax({ urikiriMinPrice, urikiriMaxPrice }) {
      return parseIntVal(urikiriMinPrice) > parseIntVal(urikiriMaxPrice);
    },
    isEmptyOrSpaces({ urikiriComment }) {
      return urikiriComment === null || urikiriComment.match(/^ *$/) !== null;
    },
    async updateUrikiriComment() {
      const urikiriComment = this.appointment.car.urikiriComment;

      if (this.isEmptyOrSpaces({ urikiriComment })) return;

      await this.updateCar({
        field: 'urikiriComment',
        value: urikiriComment,
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/tabs/EvaluationTab.vue